import { el, select, input, btn, label, colors } from "./app";
import type { PlayerDto } from "../client/ws/MessageDtos";

const ROLES_BY_ALLEGIANCE: Record<string, { value: string; label: string }[]> = {
  ELF: [
    { value: "ELF_CATCHER", label: "Catcher" },
    { value: "ELF_THROWER", label: "Thrower" },
    { value: "ELF_BLITZER", label: "Blitzer" },
    { value: "ELF_FODDER", label: "Fodder" },
  ],
  ORC: [
    { value: "ORC_BIGMAN", label: "Bigman" },
    { value: "ORC_GOBLIN", label: "Goblin" },
    { value: "ORC_BLITZER", label: "Blitzer" },
    { value: "ORC_FODDER", label: "Fodder" },
  ],
};

// Explicit cross-allegiance role mappings so switching allegiance doesn't produce duplicates.
const CROSS_ROLE_MAP: Record<string, string> = {
  ELF_CATCHER:  "ORC_GOBLIN",
  ELF_THROWER:  "ORC_BIGMAN",
  ELF_BLITZER:  "ORC_BLITZER",
  ELF_FODDER:   "ORC_FODDER",
  ORC_BIGMAN:   "ELF_THROWER",
  ORC_GOBLIN:   "ELF_CATCHER",
  ORC_BLITZER:  "ELF_BLITZER",
  ORC_FODDER:   "ELF_FODDER",
};

interface PlayerRow {
  container: HTMLDivElement;
  getName: () => string;
  getRole: () => string;
  updateRoles: (allegiance: string) => void;
}

/**
 * Builds a dynamic roster entry form.
 * Returns the container element and a `getPlayers()` function.
 */
export function buildRosterForm(initialAllegiance: string): {
  container: HTMLDivElement;
  getPlayers: () => PlayerDto[];
  updateAllegiance: (allegiance: string) => void;
} {
  const rows: PlayerRow[] = [];

  const wrapper = el("div");

  const lbl = el("p", {
    fontSize: "11px",
    color: colors.muted,
    letterSpacing: "1px",
    textTransform: "uppercase",
    margin: "0 0 8px 0",
  });
  lbl.textContent = "Players";
  wrapper.appendChild(lbl);

  const rowsContainer = el("div", { display: "flex", flexDirection: "column", gap: "8px" });
  wrapper.appendChild(rowsContainer);

  const addBtn = btn("+ Add Player", "ghost");
  addBtn.style.marginTop = "6px";
  addBtn.style.fontSize = "13px";
  addBtn.style.color = colors.accent;
  wrapper.appendChild(addBtn);

  function addRow(defaultName?: string, defaultRole?: string): void {
    if (rows.length >= 20) return;

    let currentAllegiance = getCurrentAllegiance();

    const container = el("div", {
      display: "flex",
      gap: "8px",
      alignItems: "center",
    });

    const nameInput = input(`Player ${rows.length + 1}`);
    if (defaultName) nameInput.value = defaultName;
    nameInput.style.flex = "1";
    nameInput.style.minWidth = "0";

    const roleSelect = select(ROLES_BY_ALLEGIANCE[currentAllegiance] ?? ROLES_BY_ALLEGIANCE.ELF);
    if (defaultRole) roleSelect.value = defaultRole;
    roleSelect.style.flex = "0 0 auto";

    const removeBtn = el("button", {
      background: "transparent",
      border: "none",
      color: colors.muted,
      cursor: "pointer",
      fontSize: "16px",
      padding: "4px 6px",
      lineHeight: "1",
      flexShrink: "0",
    });
    removeBtn.textContent = "×";
    removeBtn.title = "Remove player";
    removeBtn.addEventListener("click", () => {
      rowsContainer.removeChild(container);
      const idx = rows.findIndex((r) => r.container === container);
      if (idx >= 0) rows.splice(idx, 1);
    });

    container.appendChild(nameInput);
    container.appendChild(roleSelect);
    container.appendChild(removeBtn);
    rowsContainer.appendChild(container);

    const row: PlayerRow = {
      container,
      getName: () => nameInput.value.trim() || `Player${rows.indexOf(row) + 1}`,
      getRole: () => roleSelect.value,
      updateRoles: (allegiance: string) => {
        const current = roleSelect.value;
        const opts = ROLES_BY_ALLEGIANCE[allegiance] ?? ROLES_BY_ALLEGIANCE.ELF;
        roleSelect.innerHTML = "";
        for (const opt of opts) {
          const o = document.createElement("option");
          o.value = opt.value;
          o.textContent = opt.label;
          roleSelect.appendChild(o);
        }
        // Use explicit cross-allegiance mapping first, then fall back to suffix match.
        const mapped = CROSS_ROLE_MAP[current];
        const crossMatch = mapped ? opts.find((o) => o.value === mapped) : undefined;
        if (crossMatch) {
          roleSelect.value = crossMatch.value;
        } else {
          const suffix = current.split("_").slice(1).join("_");
          const suffixMatch = opts.find((o) => o.value.endsWith(suffix));
          if (suffixMatch) roleSelect.value = suffixMatch.value;
        }
        currentAllegiance = allegiance;
      },
    };
    rows.push(row);
  }

  let _currentAllegiance = initialAllegiance;
  function getCurrentAllegiance(): string {
    return _currentAllegiance;
  }

  addBtn.addEventListener("click", () => addRow());

  // Default roster: 1 of each unique role, 2 blitzers, 5 fodder (9 total)
  const defaultRoster = (allegiance: string): { name: string; role: string }[] => {
    const isOrc = allegiance === "ORC";
    const roles = {
      unique1: isOrc ? "ORC_BIGMAN"  : "ELF_CATCHER",
      unique2: isOrc ? "ORC_GOBLIN"  : "ELF_THROWER",
      blitzer: isOrc ? "ORC_BLITZER" : "ELF_BLITZER",
      fodder:  isOrc ? "ORC_FODDER"  : "ELF_FODDER",
    };
    return [
      { name: "Player 1", role: roles.unique1 },
      { name: "Player 2", role: roles.unique2 },
      { name: "Player 3", role: roles.blitzer },
      { name: "Player 4", role: roles.blitzer },
      { name: "Player 5", role: roles.fodder  },
      { name: "Player 6", role: roles.fodder  },
      { name: "Player 7", role: roles.fodder  },
      { name: "Player 8", role: roles.fodder  },
      { name: "Player 9", role: roles.fodder  },
    ];
  };
  for (const { name, role } of defaultRoster(initialAllegiance)) {
    addRow(name, role);
  }

  return {
    container: wrapper,
    getPlayers: () => rows.map((r) => ({ name: r.getName(), role: r.getRole() })),
    updateAllegiance: (allegiance: string) => {
      _currentAllegiance = allegiance;
      rows.forEach((r) => r.updateRoles(allegiance));
    },
  };
}

/** Allegiance toggle — returns the container and a getter. */
export function buildAllegianceToggle(
  onChange?: (allegiance: string) => void,
): { container: HTMLDivElement; getAllegiance: () => string } {
  let selected = "ELF";

  const wrapper = el("div", { marginBottom: "20px" });
  const lbl = label("Allegiance");
  wrapper.appendChild(lbl);

  const toggleRow = el("div", { display: "flex", gap: "10px" });

  function makeToggleBtn(value: string, display: string): HTMLButtonElement {
    const b = el("button", {
      padding: "9px 24px",
      borderRadius: "4px",
      border: `1px solid ${colors.border}`,
      background: "transparent",
      color: colors.muted,
      fontSize: "13px",
      fontFamily: "inherit",
      fontWeight: "bold",
      letterSpacing: "1px",
      cursor: "pointer",
      transition: "all 0.15s",
    });
    b.textContent = display;
    b.dataset["value"] = value;
    return b;
  }

  const elfBtn = makeToggleBtn("ELF", "ELF");
  const orcBtn = makeToggleBtn("ORC", "ORC");

  function setActive(value: string): void {
    selected = value;
    [elfBtn, orcBtn].forEach((b) => {
      const active = b.dataset["value"] === value;
      b.style.background = active ? colors.accent : "transparent";
      b.style.color = active ? "#fff" : colors.muted;
      b.style.borderColor = active ? colors.accent : colors.border;
    });
    onChange?.(value);
  }

  elfBtn.addEventListener("click", () => setActive("ELF"));
  orcBtn.addEventListener("click", () => setActive("ORC"));

  setActive("ELF");

  toggleRow.appendChild(elfBtn);
  toggleRow.appendChild(orcBtn);
  wrapper.appendChild(toggleRow);

  return { container: wrapper, getAllegiance: () => selected };
}
